import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaMapMarkerAlt, FaLinkedin, FaGlobe, FaPaperPlane, FaUser, FaComment } from 'react-icons/fa';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 2000);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="contact-form-card contact-success"
      >
        {/* Animated checkmark circle */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 260, damping: 18 }}
          className="success-icon-circle"
        >
          <motion.svg
            viewBox="0 0 52 52"
            className="success-checkmark-svg"
            initial="hidden"
            animate="visible"
          >
            <circle cx="26" cy="26" r="25" fill="none" className="success-circle-path" />
            <motion.path
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14 27 l8 8 l16 -16"
              className="success-check-path"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.4, duration: 0.4, ease: 'easeInOut' }}
            />
          </motion.svg>
        </motion.div>

        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="success-title"
        >
          Message Sent!
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="success-message"
        >
          Thank you! I'll get back to you within 24–48 hours.
        </motion.p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit}
      className="contact-form-card"
    >
      <h3 className="contact-form-title">
        Send Me a Message
      </h3>

      {/* Name Field */}
      <div className="form-field">
        <label htmlFor="name" className="form-label">
          Name *
        </label>
        <div className="form-input-wrapper">
          <div className="form-icon">
            <FaUser />
          </div>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`form-input ${errors.name ? 'form-input-error' : ''}`}
            placeholder="Your full name"
          />
        </div>
        {errors.name && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="form-error"
          >
            {errors.name}
          </motion.p>
        )}
      </div>

      {/* Email Field */}
      <div className="form-field">
        <label htmlFor="email" className="form-label">
          Email *
        </label>
        <div className="form-input-wrapper">
          <div className="form-icon">
            <FaEnvelope />
          </div>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-input ${errors.email ? 'form-input-error' : ''}`}
            placeholder="your.email@example.com"
          />
        </div>
        {errors.email && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="form-error"
          >
            {errors.email}
          </motion.p>
        )}
      </div>

      {/* Message Field */}
      <div className="form-field">
        <label htmlFor="message" className="form-label">
          Message *
        </label>
        <div className="form-input-wrapper">
          <div className="form-icon form-icon-textarea">
            <FaComment />
          </div>
          <textarea
            id="message"
            name="message"
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className={`form-input form-textarea ${errors.message ? 'form-input-error' : ''}`}
            placeholder="Tell me about your project or how I can help you..."
          />
        </div>
        {errors.message && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="form-error"
          >
            {errors.message}
          </motion.p>
        )}
      </div>

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        className={`form-submit ${isSubmitting ? 'form-submit-loading' : ''}`}
      >
        {isSubmitting ? (
          <>
            <motion.span
              className="form-submit-spinner"
              animate={{ rotate: 360 }}
              transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
            />
            <span>Sending...</span>
          </>
        ) : (
          <>
            <span>Send Message</span>
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <FaPaperPlane />
            </motion.span>
          </>
        )}
      </motion.button>
    </motion.form>
  );
};

const Contact = () => {
  const contactInfo = [
    {
      icon: FaEnvelope,
      title: 'Email',
      details: 'itratjassani@gmail.com',
      link: 'mailto:itratjassani@gmail.com'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Location',
      details: 'Karachi, Pakistan | Remote Worldwide',
      link: null
    },
    {
      icon: FaLinkedin,
      title: 'LinkedIn',
      details: 'Connect with me professionally',
      link: 'https://pk.linkedin.com/in/itrat-zehra-jassani'
    },
    {
      icon: FaGlobe,
      title: 'Availability',
      details: 'Open for consulting & collaboration',
      link: null
    }
  ];

  return (
    <div className="container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">
          Let's Build Something Impactful Together
        </h2>
        <p className="contact-subtitle">
          I'm open to collaboration, consulting, or speaking opportunities. 
          Let's discuss how we can create solutions that make a difference.
        </p>
      </motion.div>

      <div className="contact-content">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="contact-info"
          >
            <div className="contact-intro">
              <h3 className="contact-intro-title">
                Get In Touch
              </h3>
              <p className="contact-intro-text">
                Whether you're looking to transform your digital strategy, need project management expertise, 
                or want to discuss innovative healthcare solutions, I'd love to hear from you.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="contact-cards">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="contact-card"
                >
                  <div className="contact-card-content">
                    <div className="contact-card-icon">
                      <info.icon size={20} />
                    </div>
                    <div className="contact-card-text">
                      <h4 className="contact-card-title">
                        {info.title}
                      </h4>
                      {info.link ? (
                        <a
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : '_self'}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                          className="contact-card-link"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="contact-card-details">{info.details}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="contact-response-card"
            >
              <h4 className="contact-response-title">Response Time</h4>
              <p className="contact-response-text">
                I typically respond to messages within 24-48 hours. 
                For urgent matters, please mention it in your subject line.
              </p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="contact-form-wrapper"
          >
            <ContactForm />
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="contact-final-cta"
        >
          <div className="contact-final-card">
            <h3 className="contact-final-title">
              Ready to Start Your Project?
            </h3>
            <p className="contact-final-text">
              Let's schedule a consultation to discuss your goals and how I can help achieve them.
            </p>
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://pk.linkedin.com/in/itrat-zehra-jassani"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-final-button"
            >
              <span>Schedule a Call</span>
              <FaLinkedin />
            </motion.a>
          </div>
        </motion.div>
    </div>
  );
};

export default Contact;